/**
 * Instant, client-side script detection.
 *
 * This exists so the source label reads "Korean - Detected" while the user is
 * still typing, rather than after the debounce and a round trip. It deliberately
 * only covers what a script range settles outright — kana, hangul, Han — and
 * returns null for Latin text, where guessing between English and Spanish
 * without the model would mean showing a label that flips a moment later.
 *
 * The server remains the authority: whatever it reports on `meta`/`detected`
 * overwrites this.
 */

const HANGUL = /[가-힯ᄀ-ᇿ㄰-㆏]/;
const KANA = /[぀-ゟ゠-ヿ]/;
const HAN = /[一-鿿㐀-䶿]/;

/**
 * The three languages the rules above can decide on sight.
 *
 * Kept as a set because the interesting question is often the negative one: if
 * a label says Korean and the box contains no Hangul, that label is provably
 * stale — see `contradictsScript`.
 */
const SCRIPT_DECIDED = new Set(['ko', 'ja', 'zh']);

const LATIN = /\p{Script=Latin}/u;

/**
 * Which script the text is MOSTLY in — not which scripts appear in it.
 *
 * Presence was the old rule and it was wrong in a way that reached much further
 * than this label. One Korean word quoted inside an English paragraph made the
 * whole paragraph Korean, and the modes that answer in the language you wrote in
 * took that literally and replied in Korean to an English message.
 *
 * The same counting rule as the server's `detectScript` (backend
 * lib/languages.ts), and it has to stay the same: this paints the label while
 * the server is still deciding, so if the two disagree the caption flips a
 * second after it appears. CJK characters count double there and here, for the
 * reason given there — they carry about twice the text of a Latin letter, so a
 * raw count would let one English brand name outvote the Korean sentence
 * around it.
 */
export function detectScriptClient(text: string): { code: string; name: string } | null {
  if (!text.trim()) return null;

  let latin = 0, hangul = 0, kana = 0, han = 0;
  for (const ch of text) {
    if (LATIN.test(ch)) latin++;
    else if (HANGUL.test(ch)) hangul += 2;
    else if (KANA.test(ch)) kana += 2;
    else if (HAN.test(ch)) han += 2;
  }

  // Kana before Han: Japanese is full of kanji, which share the Han block with
  // Chinese, so testing Han first would label every Japanese sentence Chinese.
  const best = Math.max(hangul, kana, han);
  if (best <= latin) return null;
  if (hangul === best) return { code: 'ko', name: 'Korean' };
  if (kana === best) return { code: 'ja', name: 'Japanese' };
  return { code: 'zh', name: 'Chinese' };
}

/**
 * Has the text moved on from a label that is still on screen?
 *
 * Detection is kept when a keystroke cannot improve on it, which is right — the
 * label would otherwise flicker between every answer. But it made the label
 * *wrong* rather than merely stale in one case: type Chinese, then replace it
 * with Spanish, and "Chinese - Detected" sits there until the next round trip.
 * That used to cost a slightly wrong caption. It now also names the wrong
 * language in four mode labels and draws the wrong flag on a button.
 *
 * Narrow on purpose: this only answers for the three languages a script settles
 * outright. If the box holds no Hangul at all, "Korean" is not a guess that
 * might yet be right, it is a fact that has expired. Everything else — English
 * against Spanish — is left to the server, because a keystroke genuinely cannot
 * settle it here.
 */
export function contradictsScript(text: string, code: string | null | undefined): boolean {
  if (!code || !SCRIPT_DECIDED.has(code)) return false;
  return detectScriptClient(text)?.code !== code;
}
